import React, { useState } from 'react';
import { useRAGStore } from '../store/ragStore';
import { QueryRequest } from '../types/rag';
import { Search, RefreshCw } from 'lucide-react';
import ErrorDisplay from './ErrorDisplay';

const QueryTab: React.FC = () => {
  const {
    currentContext,
    lastUserQuery,
    lastResponse,
    query,
    isLoading,
    refreshCurrentContext,
    isOperationInProgress,
    currentOperation,
  } = useRAGStore();

  const [queryText, setQueryText] = useState('');
  // Retrieval params now live in the shared store (also editable in the Retrieval
  // Settings card on the Context Management page). Setter shims keep the JSX unchanged.
  const { retrievalParams, setRetrievalParams } = useRAGStore();
  const { topK, alpha, scoreThreshold, fileRetrievalK, fileSimilarityThreshold, contextWindow, useReranker } = retrievalParams;
  const setTopK = (v: number) => setRetrievalParams({ topK: v });
  const setFileSimilarityThreshold = (v: number) => setRetrievalParams({ fileSimilarityThreshold: v });
  const setAlpha = (v: number) => setRetrievalParams({ alpha: v });
  const setScoreThreshold = (v: number) => setRetrievalParams({ scoreThreshold: v });
  const setFileRetrievalK = (v: number) => setRetrievalParams({ fileRetrievalK: v });
  const setContextWindow = (v: number) => setRetrievalParams({ contextWindow: v });
  const setUseReranker = (v: boolean) => setRetrievalParams({ useReranker: v });
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!currentContext || !queryText.trim()) return;
    
    // Remove operation-in-progress check to allow non-blocking queries
    setError(null);

    const request: QueryRequest = {
      query: queryText.trim(),
      top_k: topK,
      alpha,
      score_threshold: scoreThreshold,
      file_retrieval_k: fileRetrievalK,
      file_similarity_threshold: fileSimilarityThreshold,
      context_window: contextWindow,
      use_reranker: useReranker
    };

    try {
      await query(request);
    } catch (error) {
      let errorMessage = 'Query failed';
      
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      } else if (error && typeof error === 'object' && 'message' in error) {
        errorMessage = (error as any).message;
      }
      
      setError(errorMessage);
      console.error('Query error details:', error);
    }
  };

  if (!currentContext) {
    return (
      <div className="card">
        <div className="text-center py-12">
          <Search className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No Context Selected
          </h3>
          <p className="text-gray-600">
            Please go to the Context Management tab to select a context before querying the RAG system.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Query Form */}
      <div className="card">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Query RAG System</h3>
          <button
            onClick={() => refreshCurrentContext()}
            className="btn-secondary flex items-center space-x-2"
            title="Refresh context data"
          >
            <RefreshCw className="h-4 w-4" />
            <span>Refresh</span>
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="query" className="block text-sm font-medium text-gray-700 mb-2">
              Query
            </label>
            <textarea
              id="query"
              value={queryText}
              onChange={(e) => setQueryText(e.target.value)}
              placeholder="Enter your question or query..."
              className="input-field h-24 resize-none"
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label htmlFor="topK" className="block text-sm font-medium text-gray-700 mb-2">
                Stage 2 · Top K chunks
              </label>
              <input
                id="topK"
                type="number"
                min="1"
                value={topK}
                onChange={(e) => setTopK(parseInt(e.target.value))}
                className="input-field"
                title="Stage 2 — how many CHUNKS are returned, after the files selected in stage 1 are searched."
              />
            </div>

            <div>
              <label htmlFor="fileRetrievalK" className="block text-sm font-medium text-gray-700 mb-2">
                Stage 1 · File retrieval K
              </label>
              <input
                id="fileRetrievalK"
                type="number"
                min="0"
                value={fileRetrievalK}
                onChange={(e) => setFileRetrievalK(parseInt(e.target.value))}
                className="input-field"
                title="Number of most relevant files to retrieve in first stage (0 = no cap, use every file)"
              />
            </div>

            <div>
              <label htmlFor="contextWindow" className="block text-sm font-medium text-gray-700 mb-2">
                Context window
              </label>
              <input
                id="contextWindow"
                type="number"
                min="0"
                max="50"
                value={contextWindow}
                onChange={(e) => setContextWindow(parseInt(e.target.value))}
                className="input-field"
                title="Number of previous and following chunks to be found near relavent chunks"
              />
            </div>

            <div>
              <label htmlFor="alpha" className="block text-sm font-medium text-gray-700 mb-2">
                Alpha (0.0 - 1.0)
              </label>
              <input
                id="alpha"
                type="number"
                min="0.0"
                max="1.0"
                step="0.1"
                value={alpha}
                onChange={(e) => setAlpha(parseFloat(e.target.value))}
                className="input-field"
              />
            </div>

            <div>
              <label htmlFor="scoreThreshold" className="block text-sm font-medium text-gray-700 mb-2">
                Stage 2 · Chunk score threshold
              </label>
              <input
                id="scoreThreshold"
                type="number"
                min="0.0"
                max="1.0"
                step="0.05"
                value={scoreThreshold}
                onChange={(e) => setScoreThreshold(parseFloat(e.target.value))}
                className="input-field"
                title="Stage 2 — absolute cosine floor applied to each CHUNK. Higher rejects more out-of-domain results."
              />
            </div>

            <div>
              <label htmlFor="fileSimilarityThreshold" className="block text-sm font-medium text-gray-700 mb-2">
                Stage 1 · File similarity threshold
              </label>
              <input
                id="fileSimilarityThreshold"
                type="number"
                min="0.0"
                max="1.0"
                step="0.05"
                value={fileSimilarityThreshold}
                onChange={(e) => setFileSimilarityThreshold(parseFloat(e.target.value))}
                className="input-field"
                title="Stage 1 — min similarity between the query and a FILE's representation. Files below it never reach chunk retrieval, so a high value looks like 'the document is not indexed'. 0 = keep every file."
              />
            </div>

            <div className="flex items-center pt-8">
              <input
                id="useReranker"
                type="checkbox"
                checked={useReranker}
                onChange={(e) => setUseReranker(e.target.checked)}
                className="h-4 w-4 text-blue-600 border-gray-300 rounded"
              />
              <label htmlFor="useReranker" className="ml-2 text-sm font-medium text-gray-700"
                title="Second-stage cross-encoder reranking (bge-reranker-v2-m3); slower, off by default">
                Use reranker
              </label>
            </div>
          </div>

          <button
            type="submit"
            disabled={!queryText.trim() || isLoading}
            className="btn-primary flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Querying...</span>
              </>
            ) : (
              <>
                <Search className="h-4 w-4" />
                <span>Submit Query</span>
              </>
            )}
          </button>
        </form>

        {/* Error Display */}
        <ErrorDisplay
          error={error}
          onDismiss={() => setError(null)}
          title="Query Error"
          variant="error"
        />

        <div className="mt-4 p-3 bg-gray-50 rounded-md">
          <p className="text-sm text-gray-600">
            <strong>Current Context:</strong> {currentContext.contextId}
          </p>
          <p className="text-sm text-gray-600">
            <strong>RAG Items:</strong> {currentContext.ragItems?.length || 0}
          </p>
        </div>
      </div>

      {/* Query Results */}
      {lastResponse && (
        <div className="card">
          <h3 className="text-lg font-semibold mb-4">Query Results</h3>
          
          <div className="mb-4 p-3 bg-blue-50 rounded-md">
            <p className="text-sm text-blue-800">
              <strong>Query:</strong> {lastUserQuery}
            </p>
          </div>

          <div className="space-y-4">
            {lastResponse?.response && lastResponse.response.length > 0 ? (
              lastResponse.response.map((result: any, index: number) => (
                <div key={index} className="p-4 border border-gray-200 rounded-lg">
                  <div className="flex justify-between items-start mb-2">
                    <span className="text-sm font-medium text-gray-700">Result {index + 1}</span>
                    <div className="flex flex-col items-end space-y-1">
                      {result.score !== undefined && (
                        <span className="text-sm text-gray-500">
                          Chunk Score: {result.score.toFixed(3)}
                        </span>
                      )}
                      {result.file_similarity !== undefined && (
                        <span className="text-sm text-blue-600 font-medium">
                          File Similarity: {result.file_similarity.toFixed(3)}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* File Information */}
                  {result.file_name && (
                    <div className="mb-3 p-2 bg-blue-50 rounded-md">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-medium text-blue-800">📄 File:</span>
                        <span className="text-sm text-blue-700 font-medium">{result.file_name}</span>
                        {result.rag_id && (
                          <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded">
                            ID: {result.rag_id}
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  <p className="text-gray-900 mb-3">{result.knowledge || "⚠️ No knowledge content"}</p>

                  {/* Source and Index Information */}
                  <div className="flex flex-wrap gap-4 text-xs text-gray-600">
                    {result.source && (
                      <div className="flex items-center space-x-1">
                        <span className="font-medium">Source:</span>
                        <span className="truncate max-w-48" title={result.source}>
                          {result.source.split('/').pop() || result.source}
                        </span>
                      </div>
                    )}
                    {result.index !== undefined && result.index !== "" && (
                      <div className="flex items-center space-x-1">
                        <span className="font-medium">Page/Index:</span>
                        <span>{result.index}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="p-4 text-gray-500 text-sm italic">
                ⚠️ No response results available.
              </div>
            )}

          </div>

          {lastResponse?.metadata && Object.keys(lastResponse.metadata).length > 0 ? (
            <div className="mt-4 p-3 bg-gray-50 rounded-md">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Metadata</h4>
              <pre className="text-xs text-gray-600 overflow-x-auto">
                {JSON.stringify(lastResponse.metadata, null, 2)}
              </pre>
            </div>
          ) : (
            <div className="mt-4 p-3 bg-gray-50 rounded-md text-xs text-gray-500 italic">
              ⚠️ No metadata available.
            </div>
          )}
          
        </div>
      )}
    </div>
  );
};

export default QueryTab;
